/**
 * Formatting helpers for Tickety filter values shown in read-only config views.
 *
 * Filter values are heterogeneous (see MarehalTicketyPOC models/filters.py):
 *  - statuses / severities / tags / ticketTypes / ticketIds: string[]
 *  - assignees / assignedGroups: { namespace, value }[]
 *  - categorizations: { key, value }[][]  (array of groups)
 *  - createdAtRange / lastUpdatedAtRange: { before?, after? }
 * so a naive Array.join renders "[object Object]". These helpers render each
 * shape into a concise, human-readable string.
 */

export function prettifyFilterKey(key: string): string {
  return key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase()).trim();
}

export function isEmptyFilterValue(value: unknown): boolean {
  if (value == null || value === '') return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') {
    return Object.values(value as Record<string, unknown>).every((v) => v == null || v === '');
  }
  return false;
}

/** Render a single array element (string, {namespace,value}, {key,value}, or nested group). */
function formatItem(item: unknown): string {
  if (item == null || item === '') return '';
  if (Array.isArray(item)) {
    // e.g. a categorization group: [{key,value}, ...]
    return item.map(formatItem).filter(Boolean).join(' + ');
  }
  if (typeof item === 'object') {
    const o = item as Record<string, unknown>;
    if ('value' in o && 'namespace' in o) {
      // NamespaceValue (assignees / assignedGroups) — the value is the meaningful name.
      return String(o.value ?? o.namespace ?? '');
    }
    if ('key' in o && 'value' in o) {
      return `${o.key}: ${o.value}`;
    }
    const entries = Object.entries(o).filter(([, v]) => v != null && v !== '');
    return entries.map(([k, v]) => `${k}: ${String(v)}`).join(', ');
  }
  return String(item);
}

/** Render a whole filter value into a readable string; "-" when empty. */
export function formatFilterValue(value: unknown): string {
  if (isEmptyFilterValue(value)) return '-';
  if (Array.isArray(value)) {
    const parts = value.map(formatItem).filter(Boolean);
    return parts.length ? parts.join(', ') : '-';
  }
  if (typeof value === 'object') {
    // Range-like object, e.g. { before, after }
    const entries = Object.entries(value as Record<string, unknown>).filter(([, v]) => v != null && v !== '');
    return entries.length ? entries.map(([k, v]) => `${k}: ${String(v)}`).join(', ') : '-';
  }
  return String(value);
}
